/**
 * What older phones lack and the widget (and @pz/scene) relies on, installed
 * before anything else runs. Imported first by main.ts.
 *
 * - `structuredClone`: missing on iOS ≤ 15.3, Chrome < 98, Samsung Internet
 *   < 18 and older Android WebViews. Without it the design could not be
 *   committed ("structuredClone is not defined") and nothing reached the cart.
 * - `globalThis`: missing on iOS 12.0–12.1.
 * - `Array.prototype.at` (iOS < 15.4, Chrome < 92) and `replaceChildren`
 *   (iOS < 14, Chrome < 86): the designer and the collage board use them.
 *
 * The clone covers what the widget clones — answers, scenes, collage and
 * designer state: plain objects and arrays, dates, maps, sets, binary
 * buffers; files and blobs are immutable and kept as they are; cycles are
 * kept as cycles.
 */

type Seen = Map<unknown, unknown>;

export function cloneDeep<T>(value: T, seen: Seen = new Map()): T {
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return seen.get(value) as T;
  const v = value as unknown;
  if (v instanceof Date) return new Date(v.getTime()) as unknown as T;
  if (v instanceof RegExp) return new RegExp(v.source, v.flags) as unknown as T;
  if (typeof Blob !== 'undefined' && v instanceof Blob) return value;
  if (v instanceof ArrayBuffer) return v.slice(0) as unknown as T;
  if (ArrayBuffer.isView(v)) {
    const view = v as unknown as { constructor: new (buffer: ArrayBuffer) => unknown; buffer: ArrayBuffer; byteOffset: number; byteLength: number };
    return new view.constructor(view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength)) as T;
  }
  if (v instanceof Map) {
    const out = new Map();
    seen.set(value, out);
    v.forEach((item, key) => out.set(cloneDeep(key, seen), cloneDeep(item, seen)));
    return out as unknown as T;
  }
  if (v instanceof Set) {
    const out = new Set();
    seen.set(value, out);
    v.forEach((item) => out.add(cloneDeep(item, seen)));
    return out as unknown as T;
  }
  if (Array.isArray(v)) {
    const out: unknown[] = [];
    seen.set(value, out);
    for (let i = 0; i < v.length; i++) out[i] = cloneDeep(v[i], seen);
    return out as unknown as T;
  }
  const out: Record<string, unknown> = {};
  seen.set(value, out);
  for (const key of Object.keys(v as Record<string, unknown>)) out[key] = cloneDeep((v as Record<string, unknown>)[key], seen);
  return out as T;
}

export function installPolyfills(): void {
  const w = (typeof window !== 'undefined' ? window : undefined) as (Window & { globalThis?: unknown }) | undefined;
  if (w && typeof w.globalThis === 'undefined') w.globalThis = w;
  const g = (typeof globalThis !== 'undefined' ? globalThis : w) as { structuredClone?: unknown } | undefined;
  if (g && typeof g.structuredClone !== 'function') g.structuredClone = (value: unknown) => cloneDeep(value);
  const define = (target: object | undefined, name: string, value: unknown): void => {
    if (!target || name in target) return;
    Object.defineProperty(target, name, { value, writable: true, configurable: true, enumerable: false });
  };
  define(Array.prototype, 'at', function at(this: ArrayLike<unknown>, index: number) {
    const n = Math.trunc(index) || 0;
    const i = n < 0 ? this.length + n : n;
    return i < 0 || i >= this.length ? undefined : this[i];
  });
  const replaceChildren = function replaceChildren(this: ParentNode & Node, ...nodes: (Node | string)[]) {
    while (this.firstChild) this.removeChild(this.firstChild);
    this.append(...nodes);
  };
  if (typeof Element !== 'undefined') define(Element.prototype, 'replaceChildren', replaceChildren);
  if (typeof DocumentFragment !== 'undefined') define(DocumentFragment.prototype, 'replaceChildren', replaceChildren);
}

installPolyfills();
